import {
  eligibleFirstBoxText,
  eligibleFirstBoxTextHawaii,
  inEligibleFirstBoxText,
  inEligibleFirstBoxTextHawaii,
  hmgp_title,
  hmgp_eligible_applied,
  hmgp_eligible_not_applied,
  hmgp_ineligible,
  disasterMetadata
} from "$data/consts.js";
import { getAbsoluteUrl } from "$utils/urls.js";
import { csv } from "d3";
import { setContext, getContext } from "svelte";
import stateList from "$data/state_info.json";

//new files go in teh static folder, dated
let content_data_file_name = "feature1_search_data_2026_09_21.csv";
let viz_data_file_name = "feature1_data_visualization_2026_09_21.csv";

export class ToolData {
  allCommunityData = $state();
  allDisasterData = $state();

  activeCommunityID = $state(null);
  activeCommunityName = $derived.by(() => {
    if (!this.isReady()) return null;
    return this.myCommunityData ? this.myCommunityData.community_name : null;
  });
  activeCommunityNameShort = $derived.by(() => {
    if (!this.isReady()) return null;
    return this.myCommunityData ? this.myCommunityData.community_name_short : null;
  });

  communityDropdownData = $derived.by(() => this.buildCommunityDropdownData());

  myCommunityData = $derived.by(() =>
    this.allCommunityData.find((d) => d.community_ID === this.activeCommunityID)
  );
  myDisasterData = $derived.by(() =>
    this.allDisasterData.filter((d) => d.community_ID === this.activeCommunityID)
  );

  firstBoxTitle = $derived.by(() => {
    if (!this.myCommunityData || this.myCommunityData.length == 0) return "";
    switch (this.myCommunityData.eda_factor_eligibility) {
      case "yes":
        return "Eligible";
      case "possibly":
        return "Possibly Eligible";
      case "no":
        return "Ineligible";
      default:
        return "";
    }
  });
  firstBoxText = $derived.by(() => this.makeFirstBoxText());

  alternateFundingSet = $derived.by(() => this.makeAlternateFundingSet());

  async fetchData() {
    this.allCommunityData = await csv(getAbsoluteUrl(`/${content_data_file_name}`), (d) => {
      return {
        community_ID: d.community_id,
        community_name: d.community_name,
        community_name_short: d.community_name_short,
        community_state: d.community_state,
        //community_rep: d.community_edr,
        //community_rep_email: d.community_edr_email,
        eda_factor_eligibility: d.eda_factor_eligibility,
        eda_declaration_name_1: d.eda_declaration_name_1,
        // eda_declaration_name_2: d.eda_declaration_name_2,
        eda_declaration_year_1: +d.eda_declaration_year_1,
        // eda_declaration_year_2: +d.eda_declaration_year_2,
        // eda_declaration_disaster_1: +d.eda_declaration_disaster_number_1,
        // eda_declaration_disaster_2: +d.eda_declaration_disaster_number_2,
        eda_spatial_intersection_counties_1: d.eda_spatial_intersection_counties_1.split(";"),
        fema_hmgp_binary: +d.fema_hmgp_binary,
        fema_hmgp_applied_binary: +d.fema_hmgp_lead_applicant_declaration_binary,
        sba_binary: +d.sba_binary
      };
    });

    this.allDisasterData = await csv(getAbsoluteUrl(`/${viz_data_file_name}`), (d) => {
      return {
        community_ID: d.community_id,
        community_name: d.community_name,
        id: +d.disaster_id,
        title: d.disaster_title,
        year_declared: +d.disaster_year_declared,
        type: d.disaster_type,
        designated_area: d.disaster_designated_area,
        lead_applicant: d.disaster_lead_applicant
      };
    });
  }

  isReady() {
    let isReady =
      this.allCommunityData &&
      this.allDisasterData &&
      this.allCommunityData.length > 0 &&
      this.allDisasterData.length > 0;
    return isReady || false;
  }

  buildCommunityDropdownData() {
    if (!this.isReady()) return [];
    return this.allCommunityData.map((d) => ({
      value: d.community_ID,
      label: d.community_name
    }));
  }

  makeFirstBoxText() {
    if (!this.isReady()) return "";
    let myData = this.myCommunityData;
    let flag = myData.eda_factor_eligibility;
    let pieces;
    switch (flag) {
      case "yes":
        pieces =
          myData.community_state == "HI"
            ? eligibleFirstBoxTextHawaii.split("[XX]")
            : eligibleFirstBoxText.split("[XX]");
        return (
          pieces[0] +
          myData.eda_declaration_name_1 +
          pieces[1] +
          myData.eda_declaration_year_1 +
          pieces[2]
        );
      case "no":
        return myData.community_state == "HI"
          ? inEligibleFirstBoxTextHawaii
          : inEligibleFirstBoxText;
      default:
        return "";
    }
  }

  makeAlternateFundingSet() {
    let set = { eligible: [], ineligible: [] };

    if (!this.isReady() || !this.activeCommunityID) return set;

    //build and sort hmgp
    let text = "";
    if (this.myCommunityData.fema_hmgp_binary == 0) {
      //not eligible
      set.ineligible.push({ title: hmgp_title, text: hmgp_ineligible });
    } else {
      //eligible
      if (this.myCommunityData.fema_hmgp_applied_binary == 1) {
        //eligible and lead applicant
        text = hmgp_eligible_applied.split("[XX]").join(this.activeCommunityNameShort);
      } else {
        //eligible but not lead applicant
        text = hmgp_eligible_not_applied;
      }
      set.eligible.push({ title: hmgp_title, text: text });
    }

    return set;
  }

  getStateNameFromAbbr(abbr) {
    const state = stateList.find((s) => s.abbr === abbr);
    return state ? state.state : null;
  }

  getColorFromType(type) {
    return disasterMetadata.filter((d) => d.type === type)[0].color;
  }
}

let contextKey = "TOOL_DATA";

export function createData() {
  const data = new ToolData();
  setContext(contextKey, data);
  return data;
}

export function useData() {
  return getContext(contextKey);
}
